import React, { useState, useRef, useEffect } from 'react';
import { Button, Table, Form, Select, Input, Space, Tag, Tabs, Tooltip } from 'antd';
import { useAntdTable, useSize, useDebounce } from 'ahooks';
import { useDispatch } from 'react-redux';
import { setBreadcrumbList } from '../../../store/menuInfo/menuInfo';
import apis from '../../../apis';
import AuditDetail from '../AuditDetail';
import './index.scss';

const { Option } = Select;

const IconView = (
    <svg t="1704265207410" viewBox="0 0 1024 1024" version="1.1" p-id="4261" width="12" height="12" style={{ cursor: 'pointer' }}>
        <path d="M896 64H128c-35.296 0-64 28.704-64 64v768c0 35.296 28.704 64 64 64h592a32 32 0 1 0 0-64H128V128h768v592a32 32 0 1 0 64 0V128c0-35.296-28.704-64-64-64z" p-id="4262"></path>
        <path d="M791.744 746.496A206.752 206.752 0 0 0 832 624c0-114.688-93.312-208-208-208S416 509.312 416 624s93.312 208 208 208a206.752 206.752 0 0 0 122.496-40.256l110.88 110.88a31.904 31.904 0 0 0 45.248 0 31.968 31.968 0 0 0 0-45.248l-110.88-110.88zM480 624c0-79.392 64.608-144 144-144s144 64.608 144 144-64.608 144-144 144-144-64.608-144-144zM800 264a32 32 0 0 0-32-32H256a32 32 0 0 0 0 64h512a32 32 0 0 0 32-32zM256 422.656a32 32 0 0 0 0 64h96a32 32 0 0 0 0-64H256z" p-id="4263"></path>
    </svg>
);

const IconReview = (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-id-card-lanyard-icon lucide-id-card-lanyard"><path d="M13.5 8h-3"/><path d="m15 2-1 2h3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h3"/><path d="M16.899 22A5 5 0 0 0 7.1 22"/><path d="m9 2 3 6"/><circle cx="12" cy="15" r="3"/></svg>
);

const TocAuditList = () => {
    const [viewState, setViewState] = useState('list'); // 'list' | 'view' | 'audit'
    const [currentAuditId, setCurrentAuditId] = useState(null);
    const [activeTab, setActiveTab] = useState('PROCESSED');
    const [productList, setProductList] = useState([]);

    const refTable = useRef(null);
    const refHeader = useRef(null);
    const size = useSize(refTable);
    const headerSize = useSize(refHeader);
    const dispatch = useDispatch();

    useEffect(() => {
        if (viewState === 'list') {
            dispatch(setBreadcrumbList([{ title: '充值方案' }, { title: 'TOC方案审核' }]));
        }
    }, [viewState, dispatch]);

    useEffect(() => {
        apis.getProductList('TOC').then(res => {
            if (res.code === 200) {
                const items = Array.isArray(res.data?.items) ? res.data.items : (Array.isArray(res.data) ? res.data : []);
                setProductList(items);
            }
        }).catch(() => {});
    }, []);

    const [form] = Form.useForm();

    const getTableData = ({ current, pageSize }, formData) => {
        const params = {
            page: current,
            page_size: pageSize,
            ...formData,
            audit_scope: activeTab,
        };

        Object.keys(params).forEach(key => {
            if (params[key] === undefined || params[key] === null || params[key] === '') {
                delete params[key];
            }
        });

        return apis.getTocTopupAuditList(params).then(res => {
            if (res.code === 200) {
                return {
                    total: res.data?.total || 0,
                    list: res.data?.items || []
                };
            }
            return {
                total: 0,
                list: []
            };
        }).catch(() => {
            return {
                total: 0,
                list: []
            };
        });
    };

    const { tableProps, search, refresh } = useAntdTable(getTableData, {
        form,
        defaultPageSize: 10,
        manual: true,
    });

    const { submit, reset } = search;

    const topup_plan_name = Form.useWatch('topup_plan_name', form);
    const debounced_topup_plan_name = useDebounce(topup_plan_name, { wait: 500 });

    useEffect(() => {
        submit();
    }, [
        activeTab,
        Form.useWatch('prd_id', form),
        Form.useWatch('topup_plan_type_code', form),
        Form.useWatch('topup_plan_status_code', form),
        debounced_topup_plan_name,
    ]);

    const handleView = (record) => {
        setCurrentAuditId(record.topup_plan_audit_id);
        setViewState('view');
    };

    const handleAudit = (record) => {
        setCurrentAuditId(record.topup_plan_audit_id);
        setViewState('audit');
    };

    if (viewState === 'view' || viewState === 'audit') {
        return <AuditDetail
            id={currentAuditId}
            mode={viewState}
            onBack={() => {
                setViewState('list');
                refresh();
            }}
        />;
    }

    const getPlanTypeText = (code) => {
        if (code === 'PERSONAL' || code === 'PERSONAL_EDITION') return '个人版';
        if (code === 'SEAT' || code === 'ENTERPRISE_EDITION') return '企业版';
        return code || '-';
    };

    const columns = [
        {
            title: '产品',
            dataIndex: 'prd_name',
            align: 'center',
            render: (text, record) => record.prd_name || record.prd_id || '-',
        },
        {
            title: '方案类别',
            dataIndex: 'topup_plan_type_code',
            align: 'center',
            render: (text) => getPlanTypeText(text),
        },
        {
            title: '方案编码',
            dataIndex: 'topup_plan_code',
            align: 'center',
        },
        {
            title: '方案名称',
            dataIndex: 'topup_plan_name',
            align: 'center',
        },
        {
            title: '积分',
            dataIndex: 'credits',
            align: 'center',
        },
        {
            title: '原价 (元)',
            dataIndex: 'original_price',
            align: 'center',
            render: (text) => text !== undefined && text !== null ? `¥${Number(text).toFixed(2)}` : '-',
        },
        {
            title: '折扣价 (元)',
            dataIndex: 'discount_price',
            align: 'center',
            render: (text) => text !== undefined && text !== null && text !== '' ? `¥${Number(text).toFixed(2)}` : '-',
        },
        {
            title: '提交审核版本',
            dataIndex: 'submit_ver_no',
            align: 'center',
        },
        {
            title: '发布说明',
            dataIndex: 'comment',
            align: 'center',
            render: (text) => text || '-',
        },
        {
            title: '方案状态',
            dataIndex: 'topup_plan_status_code',
            align: 'center',
            render: (text) => {
                console.log('text', text);
                const map = {
                    'LISTED': { color: 'green', text: '已上架' },
                    'UNLISTED': { color: 'default', text: '未上架' },
                    'DELETED': { color: 'red', text: '已删除' },
                };
                return <Tag color={map[text]?.color}>{map[text]?.text || text || '-'}</Tag>;
            }
        },
        {
            title: '提交人',
            dataIndex: 'auditer_name',
            align: 'center',
        },
        {
            title: '提交时间',
            dataIndex: 'audit_time',
            align: 'center',
            render: (text) => text || '-',
        },
        {
            title: '审核结果',
            dataIndex: 'audit_status_code',
            align: 'center',
            render: (text) => {
                const map = {
                    'PENDING': { color: 'orange', text: '待审核' },
                    'PUBLISHED': { color: 'green', text: '通过' },
                    'REJECTED': { color: 'red', text: '驳回' },
                };
                return <Tag color={map[text]?.color}>{map[text]?.text || text || '-'}</Tag>;
            }
        },
        {
            title: '操作',
            key: 'action',
            width: 80,
            align: 'center',
            render: (_, record) => {
                const isPending = record.audit_result_code === 'PENDING' || activeTab === 'PENDING';
                return (
                    <div className="tableTagsBox">
                        {isPending && (
                            <Tooltip title="审核">
                                <span onClick={() => handleAudit(record)} className="action-icon">
                                    {IconReview}
                                </span>
                            </Tooltip>
                        )}
                        {!isPending && (
                            <Tooltip title="查看">
                                <span onClick={() => handleView(record)} className="action-icon">
                                    {IconView}
                                </span>
                            </Tooltip>
                        )}
                    </div>
                );
            },
        },
    ];

    return (
        <div className="toc-audit-list-container" ref={refTable}>
            <div className="filterItemsBox" ref={refHeader}>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        // { key: 'PENDING', label: '待审核' },
                        { key: 'PROCESSED', label: '已审核' },
                        { key: 'MY_SUBMITTED', label: '我发起的' },
                    ]}
                    style={{ width: '100%', marginBottom: '8px' }}
                />

                <div className="filterItemsContent">
                    <Form form={form} component={false}>
                        <div className="filterItem">
                            <span className="filterItemSpan">产品</span>
                            <Form.Item name="prd_id" noStyle>
                                <Select allowClear placeholder="全部" style={{ width: '180px' }} showSearch optionFilterProp="children">
                                    {productList.map(p => (
                                        <Option key={p.prd_id} value={p.prd_id}>
                                            {p.prd_name || p.prd_id}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </div>

                        <div className="filterItem">
                            <span className="filterItemSpan">方案类别</span>
                            <Form.Item name="topup_plan_type_code" noStyle>
                                <Select allowClear placeholder="全部" style={{ width: '180px' }}>
                                    <Option value="PERSONAL">个人版</Option>
                                    <Option value="SEAT">企业版</Option>
                                </Select>
                            </Form.Item>
                        </div>

                        <div className="filterItem">
                            <span className="filterItemSpan">方案状态</span>
                            <Form.Item name="topup_plan_status_code" noStyle>
                                <Select allowClear placeholder="全部" style={{ width: '180px' }}>
                                    <Option value="LISTED">已上架</Option>
                                    <Option value="UNLISTED">未上架</Option>
                                    <Option value="DELETED">已删除</Option>
                                </Select>
                            </Form.Item>
                        </div>

                        <div className="filterItem">
                            <span className="filterItemSpan">方案名称</span>
                            <Form.Item name="topup_plan_name" noStyle>
                                <Input placeholder="请输入方案名称" allowClear style={{ width: '180px' }} />
                            </Form.Item>
                        </div>
                    </Form>
                </div>
            </div>

            <div className="tableContent">
                <Table
                    columns={columns}
                    rowKey="topup_plan_audit_id"
                    {...tableProps}
                    className="myFormTable"
                    bordered={true}
                    scroll={{ x: 1400, y: size?.height ? size.height - (headerSize?.height || 0) - 180 + 'px' : undefined }}
                />
            </div>
        </div>
    );
};

export default TocAuditList;
